import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { standardGetHttpOptions, standardHttpOptions, EventInfo, EventIgnore, EventActionStatus, WaterSchedule, LightFloodSchedule } from '../../shared/models';
import { TaskResults } from '../../shared/models/Event/TaskResult.model';
import { EventLogSearchCriteria } from '../../shared/models/EventLog/EventLogSearchCriteria.model';
import { LoadingService } from '../../shared/services/loading.service';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';


@Injectable()
export class TriggerService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  // Cached variables
  public events: EventInfo[] = [];

  
  constructor(private http: HttpClient, private loadingService: LoadingService, private userService: UserService, 
                @Inject(KIG_SETTINGS) private settings: KigSettings) {

    this.apiUrl = this.settings.apiUrl;
  }


 public clearCachedVariables() {
    this.events = [];
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }

 
  public getEvents(loadMessage: string, forceReload: boolean = false): Observable<any> {
    if (this.events.length > 0 && !forceReload) {
      //console.log('cached events');
      return new Observable<EventInfo[]>((obs) => {
        obs.next(this.events);
        obs.complete(); 
      });
    }

    if (loadMessage !== null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.get(`${ this.apiUrl }/event`, standardGetHttpOptions).pipe(
      map((response: EventInfo[]) => {
        //console.log('fetched events');
        this.events = response;
        return this.events;
      }),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage !== null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public updateEvent(eventInfo: EventInfo, loadMessage: string, username: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${ this.apiUrl }/event`, JSON.stringify(eventInfo), this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public updateEventsIgnoreDate(eventIgnore: EventIgnore, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }

    return this.http.put(`${ this.apiUrl }/event/ignoreDate`, JSON.stringify(eventIgnore), standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }

  
  public createEvent(eventInfo: EventInfo) {
    this.loadingService.setMessage('Creating event ...');
    return this.http.post(`${ this.apiUrl }/event/`, JSON.stringify(eventInfo), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public deleteEvent(eventInfo: EventInfo, username: string) {
    this.loadingService.setMessage('Deleting event ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${ this.apiUrl }/event/${ eventInfo.id }`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public evaluateEvent(id: number, loadMessage: string) {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }

    return this.http.get<TaskResults>(`${ this.apiUrl }/event/evaluate/${ id }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public invokeEventActions(id: number) {
    this.loadingService.setMessage('Manually invoking event action(s) ...');
    return this.http.get<TaskResults>(`${ this.apiUrl }/event/invoke/${ id }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getEventLogs(parameters: EventLogSearchCriteria): Observable<any> {
    this.loadingService.setMessage('Loading Logs...');
    return this.http.post(`${ this.apiUrl }/event/eventLogs`, JSON.stringify(parameters), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public GetLastEventLogForEachEvent(eventIds: number[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/event/LastEventLogs`, JSON.stringify(eventIds), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getEventTooltipHistory(eventIds: number[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/event/EventTooltipHistory`, JSON.stringify(eventIds), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }

  
  public refreshTriggerStatus(eventId: number): Observable<any> {
    return this.http.get<EventActionStatus>(`${ this.apiUrl }/event/actionstatus/${ eventId }`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public invokeManualFloodIrrigation(eventId: number, lightFloodSchedules: LightFloodSchedule[]): Observable<any> {
    this.loadingService.setMessage('Executing manual flood irrigation ...');
    return this.http.post(`${ this.apiUrl }/event/manualFloodIrrigation/${ eventId }`, JSON.stringify(lightFloodSchedules), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
